'use client';

import * as React from 'react';
import { Download, Disc, MicOff, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { TableCellStatus } from '@/shared/ui/table';
import type { RecordingMode, RecordingStatus } from '../types';
import { durationOrDash } from '../format';
import { analyticsApi } from '../api';
import { Section } from './Section';
import { AudioWaveformPlayer } from './AudioWaveformPlayer';
import { VideoPlayer } from './VideoPlayer';

const STATUS_META: Record<
  RecordingStatus,
  { variant: 'success' | 'warning' | 'info' | 'error' | 'neutral'; label: string }
> = {
  completed: { variant: 'success', label: 'Ready' },
  processing: { variant: 'warning', label: 'Processing' },
  recording: { variant: 'info', label: 'Recording' },
  failed: { variant: 'error', label: 'Failed' },
  none: { variant: 'neutral', label: 'None' },
};

/**
 * The recording block, one branch per `recording_status`. Playback + download
 * point at OUR proxy route (which attaches the API key server-side), never the
 * backend `recording_url` — a bare `<audio src>` at the backend would 401.
 */
export function RecordingSection({
  meetingId,
  status,
  durationSeconds,
  mode,
  leadName,
}: {
  meetingId: string;
  status: RecordingStatus;
  durationSeconds: number;
  mode: RecordingMode;
  leadName: string;
}) {
  const meta = STATUS_META[status];
  return (
    <Section
      title="Recording"
      icon={<Disc className="h-4 w-4" aria-hidden="true" />}
      aside={<TableCellStatus variant={meta.variant}>{meta.label}</TableCellStatus>}
    >
      {status === 'completed' ? (
        <CompletedRecording
          meetingId={meetingId}
          durationSeconds={durationSeconds}
          mode={mode}
          leadName={leadName}
        />
      ) : (
        <PendingRecording status={status} />
      )}
    </Section>
  );
}

function CompletedRecording({
  meetingId,
  durationSeconds,
  mode,
  leadName,
}: {
  meetingId: string;
  durationSeconds: number;
  mode: RecordingMode;
  leadName: string;
}) {
  const [loadFailed, setLoadFailed] = React.useState(false);
  const src = analyticsApi.recordingProxyUrl(meetingId);
  const isVideo = mode === 'audio_video';
  const label = isVideo ? 'video' : 'audio';

  return (
    <div className="space-y-4" aria-live="polite">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-[var(--text-secondary)]">Call {label}</p>
        <span className="text-xs tabular-nums text-[var(--text-muted)]">
          Length {durationOrDash(durationSeconds)}
        </span>
      </div>

      {loadFailed ? (
        <div
          role="alert"
          className="flex items-center gap-2.5 rounded-[var(--radius-md)] border border-[color-mix(in_srgb,var(--error)_25%,transparent)] bg-[var(--error-soft)] px-4 py-3 text-sm text-[var(--error)]"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          The recording couldn&apos;t be loaded for playback. Try downloading it instead.
        </div>
      ) : isVideo ? (
        <VideoPlayer
          src={src}
          onError={() => setLoadFailed(true)}
          ariaLabel={`Recording of the call with ${leadName || 'the lead'}`}
        />
      ) : (
        <AudioWaveformPlayer
          src={src}
          seed={meetingId}
          onError={() => setLoadFailed(true)}
          ariaLabel={`Recording of the call with ${leadName || 'the lead'}`}
        />
      )}

      <Button asChild variant="outline" size="sm">
        {/* Same-origin proxy → the download attribute forces a save. */}
        <a href={src} download={`recording-${meetingId.slice(0, 8)}`}>
          <Download className="h-4 w-4" aria-hidden="true" />
          Download {label}
        </a>
      </Button>
    </div>
  );
}

function PendingRecording({ status }: { status: Exclude<RecordingStatus, 'completed'> }) {
  const copy: Record<typeof status, { icon: React.ReactNode; title: string; body: string }> = {
    recording: {
      icon: <Loader2 className="h-5 w-5 animate-spin text-[var(--info)]" aria-hidden="true" />,
      title: 'Recording in progress',
      body: 'This call is being recorded. The audio will be available once the call ends and finishes processing.',
    },
    processing: {
      icon: <Loader2 className="h-5 w-5 animate-spin text-[var(--warning)]" aria-hidden="true" />,
      title: 'Processing the recording',
      body: 'The audio is being finalized. This can take a few minutes — check back shortly.',
    },
    failed: {
      icon: <AlertTriangle className="h-5 w-5 text-[var(--error)]" aria-hidden="true" />,
      title: 'Recording failed',
      body: 'This call’s recording could not be processed, so there’s nothing to play back.',
    },
    none: {
      icon: <MicOff className="h-5 w-5 text-[var(--text-muted)]" aria-hidden="true" />,
      title: 'No recording',
      body: 'No audio was recorded for this meeting.',
    },
  };
  const c = copy[status];

  return (
    <div className="flex items-start gap-3.5 rounded-[var(--radius-md)] border border-dashed border-[var(--border)] bg-[var(--surface-hover)] px-4 py-4">
      <span className="mt-0.5 shrink-0">{c.icon}</span>
      <div className="space-y-1">
        <p className="text-sm font-semibold text-[var(--text-primary)]">{c.title}</p>
        <p className="text-sm leading-relaxed text-[var(--text-muted)]">{c.body}</p>
      </div>
    </div>
  );
}
